import { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { Table, TableBody, TableCell, TableContainer, Typography, Box, TableHead, TableRow, Paper, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Collapse, Tabs, Tab, } from '@mui/material'
import { NoRecordFound, STableLoader } from '../../../shared_elements';
import { numberWithCommas } from '../../../utils';


const DerateRatioPivotTable = ({ rows, getStickyStyle }) => {
  const years = [...new Set(rows?.flatMap(d => d.ratios.flatMap(r => r.escalations.map(e => e.year))))]?.sort();
  const ratios = [...new Set(rows?.flatMap(d => d.ratios.map(r => r.ratio)))]?.sort((a, b) => a - b);
  const getRate = (derate, ratio, year) => {
    const derateRow = rows?.find(d => d.derate === derate);
    const ratioRow = derateRow?.ratios.find(r => r.ratio === ratio);
    const esc = ratioRow?.escalations.find(e => e.year === year);
    return esc && esc.rate !== undefined && esc.rate !== '' ? numberWithCommas(Number(esc.rate).toFixed(2)) : "-";
  };
  const [year, setYear] = useState(null);
  useEffect(() => {
    if (!years.length) return;
    const currentYear = new Date().getFullYear();
    const defaultYear = years.includes(currentYear) ? currentYear : years[0];
    setYear(defaultYear);
  }, [rows]);
  return (
    <>
      <Paper style={{ marginBottom: '4px', boxShadow: "0px 0px 2px 0.5px #BBBBBB" }}>
        <Tabs className="reserve-rate-invoice-tab" sx={{ borderBottom: 1, borderColor: 'divider' }} value={year} onChange={(e, val) => setYear(val)}  >
          {years?.map(y => (
            <Tab sx={{ textTransform: "none" }} key={y} label={y} value={y} />
          ))}
        </Tabs>
      </Paper>
      <TableContainer  sx={{ maxWidth: "100%", overflowX: "auto", position: "relative" }}  component={Paper} square  className="scrollable-table rental-scrollable-table-container">
        <Table  sx={{ minWidth: ratios.length * 110 }} size="small" className="mui-table-format" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={getStickyStyle(0)}>
                Ratio / Derate
              </TableCell>
              <TableCell sx={getStickyStyle(1)}>Escalation</TableCell>
              <TableCell sx={getStickyStyle(2)}>Esc Start Date</TableCell>
              {ratios.map(r => (
                <TableCell key={r} align="center" sx={{ fontWeight: 700 }}>
                  {`${r}%`}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map(row => (
              <TableRow key={row.derate} hover>
                <TableCell sx={getStickyStyle(0)}>
                  {row.derate}
                </TableCell>
                <TableCell sx={getStickyStyle(1)}>{row.rate_escalation_percentage ? `${row.rate_escalation_percentage}%` : "--"}</TableCell>
                <TableCell sx={getStickyStyle(2)}>{row.rate_escalation_start_date ? moment(row.rate_escalation_start_date).format('MMM DD, YYYY') : "--"}</TableCell>
                {ratios.map(ratio => (
                  <TableCell key={ratio} align="center">
                    {getRate(row.derate, ratio, year)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

const EscalationModal = ({ escalationData, onToggleModal, handleItemClicked, skeletonLoader }) => {
  let STICKY_COL_WIDTHS
  const escalationList = escalationData?.data?.list || [];
  const isFixedRental = escalationData?.item?.reserve_rate_type?.value === 1 || (escalationData?.item?.reserve_rate_based_on?.value === 4 && escalationData?.item?.fixed_rate)
  const isRatioDerate = escalationData?.item?.reserve_rate_type?.value === 3
  const isRangeBasedFCFHRatio = isRatioDerate && escalationData?.item?.reserve_rate_based_on?.value === 8
  const isFHBasedRatio = isRatioDerate && escalationData?.item?.reserve_rate_based_on?.value === 9
  const isDerateBased = isRatioDerate && escalationData?.item?.reserve_rate_based_on?.value === 11
  const hasTypeField = escalationList.some(row => row.hasOwnProperty("type"));
  const fhData = hasTypeField ? escalationList?.filter(r => r.type === 2) : [];
  const fcData = hasTypeField ? escalationList?.filter(r => r.type === 3) : [];
  if (isFixedRental) { STICKY_COL_WIDTHS = [80, 100, 120,]; } else if (isRatioDerate) {
    if (isDerateBased) { STICKY_COL_WIDTHS = [100,90,90]; } else STICKY_COL_WIDTHS = [80, 80, 80, 90,];
  }
  else { STICKY_COL_WIDTHS = [165, 50, 50, 50, 80, 100]; }
  const getStickyStyle = (index) => ({ position: 'sticky', left: STICKY_COL_WIDTHS.slice(0, index).reduce((a, b) => a + b, 0), minWidth: STICKY_COL_WIDTHS[index], background: 'inherit', zIndex: 3, });
  const years = useMemo(() => {
    if (!escalationList.length) return [];
    const allYears = escalationList.flatMap(row => row.escalations?.map(e => e.year) || []);
    return [...new Set(allYears)]?.sort((a, b) => a - b);
  }, [escalationData?.data?.list]);

  const renderYearHeaders = () => years?.map(year => (<TableCell sx={{ minWidth: 50 }} key={year} align="center" className="nowrap-cell"> {year} </TableCell>));
  const renderYearCells = (row) =>
    years?.map(year => {
      const match = row.escalations?.find(e => e.year === year);
      return (<TableCell sx={{ minWidth: 50 }} key={year} align="center"> {match && match.rate !== undefined && match.rate !== '' ? numberWithCommas(Number(match.rate).toFixed(2)) : '--'} </TableCell>);
    });
  const EscalationTable = ({ rows, title }) => (
    <>
      {title && (<Typography className='section-header-text'> FOR {title}: </Typography>)}
      <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }} className="scrollable-table rental-scrollable-table-container">
        <Table size="small" className="mui-table-format" stickyHeader>
          <TableHead>
            <TableRow>
              {!isFixedRental ? (
                <>
                  {isRatioDerate && !isRangeBasedFCFHRatio  ? <TableCell sx={getStickyStyle(0)}>{isFHBasedRatio ? "Ratio" : "DeRate"}</TableCell>
                    :
                    <>
                      <TableCell sx={getStickyStyle(0)}>Range Conditions</TableCell>
                      <TableCell sx={getStickyStyle(1)}>MIN</TableCell>
                      <TableCell sx={getStickyStyle(2)}>MAX</TableCell>
                    </>}
                    <TableCell sx={getStickyStyle(isRatioDerate ? 1 :  3)}>Rate</TableCell>
                  <TableCell sx={getStickyStyle(isRatioDerate ? 2 :4)}>Escalation</TableCell>
                  <TableCell sx={getStickyStyle(isRatioDerate ? 3 : 5)}>Esc Start Date</TableCell>
                </>
              ) : (
                <>
                  <TableCell sx={getStickyStyle(0)}>Escalation</TableCell>
                  <TableCell sx={getStickyStyle(1)}>Esc Start Date</TableCell>
                  <TableCell sx={getStickyStyle(2)}>Escalation Interval</TableCell>
                </>
              )}
              {renderYearHeaders()}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 && rows.map((row, index) => {
              const escalationPercentage = row.rate_escalation_percentage ?? row.esc_percentage;
              const escalationDate = row.rate_escalation_start_date ?? row.esc_date;
              return (
                <TableRow key={index}>
                  {!isFixedRental ? (
                    <>
                      {isRatioDerate && !isRangeBasedFCFHRatio ? <TableCell sx={getStickyStyle(0)} className="nowrap-cell">{isFHBasedRatio ? row.ratio : row.derate}</TableCell>
                        : <>
                          <TableCell sx={getStickyStyle(0)} className="nowrap-cell">{row.range_label}</TableCell>
                          <TableCell sx={getStickyStyle(1)}>{row.min || "0"}</TableCell>
                          <TableCell sx={getStickyStyle(2)}> {row.range_label === "Greater Than Equal To" ? "∞" : row.max || "0"} </TableCell></>}

                      <TableCell sx={getStickyStyle(isRatioDerate ? 1 :3)}>{row.rate !== undefined && row.rate !== '' && row.rate !== null ? numberWithCommas(Number(row.rate).toFixed(2)) : "--"}</TableCell>
                      <TableCell sx={getStickyStyle(isRatioDerate ? 2 :4)}>{escalationPercentage ? `${escalationPercentage}%` : "--"}</TableCell>
                      <TableCell sx={getStickyStyle(isRatioDerate ? 3 :5)}> {escalationDate ? moment(escalationDate).format('MMM DD, YYYY') : "--"} </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={getStickyStyle(0)}>{row.rate_escalation_percentage ? `${row.rate_escalation_percentage}%` : "--"}</TableCell>
                      <TableCell sx={getStickyStyle(1)}>{row.rate_escalation_start_date ? moment(row.rate_escalation_start_date).format('MMM DD, YYYY') : "--"}</TableCell>
                      <TableCell sx={getStickyStyle(2)}>{row.rate_escalation_interval || "--"}</TableCell>
                    </>
                  )}
                  {renderYearCells(row)}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length === 0 && <NoRecordFound />}
    </>
  );
  return (
    <Dialog aria-labelledby="customized-dialog-title" className='add-lease-dialog' open={true} maxWidth="lg" fullWidth >
      <DialogTitle sx={{ mb: 1, p: 2 }} className='dialog-header' id="customized-dialog-title">Escalated Rate</DialogTitle>
      <DialogContent >
        {skeletonLoader ? <STableLoader count={10} rowCount={3} /> :
          (
            <>
              {isDerateBased ? <DerateRatioPivotTable rows={escalationList} getStickyStyle={getStickyStyle}/> : <>
                {!hasTypeField && (<EscalationTable rows={escalationList} />)}
                {hasTypeField && (
                  <>
                    {fhData.length > 0 && <Box sx={{ mb: 1 }}><EscalationTable rows={fhData} title="FH" /></Box>}
                    {fcData.length > 0 && <EscalationTable rows={fcData} title="FC" />}
                  </>
                )}
              </>}
            </>
          )}
      </DialogContent>
      <DialogActions sx={{ padding: 1, justifyContent: 'flex-end', }}>
        <Button className='cancel-btn' size='small' onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }} variant='outlined'>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default EscalationModal



